/**
 * Service métier pour les établissements
 * Encapsule la logique d'assemblage et les opérations complexes
 */

import { establishmentsClient, classesClient } from '../api'
import type {
  Establishment,
  CreateEstablishmentDto,
  UpdateEstablishmentDto,
} from '@sphere/school/domain'

import type { Class } from '@sphere/school/domain'
import type { EstablishmentStats } from '@sphere/school/domain'

export const establishmentsService = {
  /**
   * Récupère tous les établissements accessibles
   */
  async getEstablishments(): Promise<Establishment[]> {
    return establishmentsClient.list()
  },

  /**
   * Récupère un établissement par ID
   */
  async getEstablishment(id: string): Promise<Establishment> {
    return establishmentsClient.getById(id)
  },

  /**
   * Récupère un établissement avec toutes ses données enrichies
   * (classes, stats, etc.)
   */
  async getEstablishmentWithDetails(establishmentId: string): Promise<{
    establishment: Establishment
    stats: EstablishmentStats
    classes: Class[]
  }> {
    const [establishment, classes] = await Promise.all([
      establishmentsClient.getById(establishmentId),
      classesClient.list({ establishmentId }),
    ])

    // Calculer les stats côté client à partir des données récupérées
    // Note: Pour les comptes précis d'élèves/enseignants, il faudrait récupérer les memberships
    // mais on garde une version simple ici
    const stats: EstablishmentStats = {
      establishmentId: establishment.id,
      establishmentName: establishment.name,
      totalClasses: classes.data.length,
      totalStudents: 0,
      totalTeachers: 0,
      totalStaff: 0,
      totalTeachings: 0,
      teachingsBySubject: [],
      teachingsByLevel: [],
      teachingsBySchoolYear: [],
    }

    return { establishment, stats, classes: classes.data }
  },

  /**
   * Crée un établissement
   */
  async createEstablishment(
    dto: CreateEstablishmentDto
  ): Promise<Establishment> {
    return establishmentsClient.create(dto)
  },

  /**
   * Met à jour un établissement
   */
  async updateEstablishment(
    id: string,
    dto: UpdateEstablishmentDto
  ): Promise<Establishment> {
    return establishmentsClient.update(id, dto)
  },

  /**
   * Supprime un établissement
   */
  async deleteEstablishment(id: string): Promise<void> {
    return establishmentsClient.delete(id)
  },

  /**
   * Récupère les établissements accessibles par l'utilisateur
   * Filtre basé sur le scope (bootstrap.scope.establishmentIds)
   *
   * @param userEstablishmentIds - IDs depuis bootstrap (null = admin)
   */
  async getUserEstablishments(
    userEstablishmentIds: string[] | null
  ): Promise<Establishment[]> {
    const allEstablishments = await establishmentsClient.list()

    // Admin = tous les établissements
    if (userEstablishmentIds === null) {
      return allEstablishments
    }

    // Filtrer selon le scope utilisateur
    return allEstablishments.filter(est =>
      userEstablishmentIds.includes(est.id)
    )
  },

  /**
   * Récupère un établissement par UAI
   */
  async getEstablishmentByUai(uai: string): Promise<Establishment> {
    return establishmentsClient.getByUai(uai)
  },
}
